import React from "react";
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";

export type RowAction = {
  key: string;
  label: string;
  icon: React.ReactNode;
  color?: React.ComponentProps<typeof IconButton>["color"];
  onClick: () => void;
  /** Held while something the action started is still in flight. */
  disabled?: boolean;
};

interface RowActionsMenuProps {
  actions: RowAction[];
  /** Names the menu for assistive tech when a row has its own context. */
  ariaLabel?: string;
}

/**
 * Row actions as a single overflow menu.
 *
 * Two reasons to reach for it: a narrow viewport, where a multi-icon column
 * would push the grid past the fold; and a row whose actions are secondary,
 * where spelling them out would give them the same weight as the ones that
 * matter. Both end in the same place — one button, actions behind it.
 */
export const RowActionsMenu: React.FC<RowActionsMenuProps> = ({
  actions,
  ariaLabel = "Row actions",
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  if (actions.length === 0) return null;

  return (
    <>
      <Tooltip title={ariaLabel}>
        <IconButton
          size="small"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          // No native `title`: MUI warns when a Tooltip child carries one, the
          // two tooltips compete, and a hardcoded string would override what
          // the caller named this menu.
          aria-label={ariaLabel}
          sx={{ color: "#1976d2" }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.key}
            disabled={action.disabled}
            onClick={() => {
              action.onClick();
              setAnchorEl(null);
            }}
          >
            <ListItemIcon
              sx={action.color ? { color: `${action.color}.main` } : undefined}
            >
              {action.icon}
            </ListItemIcon>
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
